import { Box, InputAdornment, useTheme } from '@ltvco/refresh-lib/theme';
import { RapidsInput, RapidsSearchData } from '@ltvco/refresh-lib/v1';
import {
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
} from '@ltvco/refresh-lib/vendors';
import SearchIcon from '@mui/icons-material/Search';
import React, { RefObject, useRef } from 'react';

import { withErrorHandling, withClearButton, composeHOC } from '../hoc';

import {
  PROPERTY_SEARCH_TYPES,
  PropertyFormData,
  rapidsInputCustomStyles,
  RapidsStateRef,
} from './utils';

interface PropertySearchInputProps {
  placeholder: string;
  errors: FieldErrors<PropertyFormData>;
  // eslint-disable-next-line no-unused-vars
  clearErrors: (field?: string) => void;
  onClear: () => void;
  trigger: UseFormTrigger<PropertyFormData>;
  onSubmit?: () => void;
  // eslint-disable-next-line no-unused-vars
  onPropertyDataSelected: (data: RapidsSearchData) => void;
  searchType: string;
  setValue: UseFormSetValue<PropertyFormData>;
  value: string;
  rapidsStateRef: RefObject<RapidsStateRef>;
}

export const PropertySearchInput = React.memo(
  ({
    placeholder,
    errors,
    clearErrors,
    onClear,
    trigger,
    onSubmit,
    onPropertyDataSelected,
    searchType,
    value,
    setValue,
    rapidsStateRef,
  }: PropertySearchInputProps) => {
    const theme = useTheme();
    const propertyDataRef = useRef<RapidsSearchData | null>(null);

    const handleRapidsDataSubmit = (propertyData: RapidsSearchData) => {
      const { street_line, city, state, zipcode } = propertyData;
      if (street_line.trim() || city.trim() || state.trim() || zipcode.trim()) {
        clearErrors('address');
      }

      propertyDataRef.current = propertyData;
      onPropertyDataSelected(propertyData);

      const rapidsInputValue = rapidsStateRef.current?.getValue();
      setValue('address', rapidsInputValue || '');

      trigger().then((isValid) => {
        if (isValid && onSubmit) {
          onSubmit();
        }
      });
    };

    const onClearHandler = () => {
      onClear();
      propertyDataRef.current = null;
      setValue('address', '');
    };

    const BaseRapidsInput = () => {
      return (
        <Box sx={{ width: '100%' }}>
          <RapidsInput
            rapidsStateRef={rapidsStateRef}
            valueOveride={value}
            addressOnly={searchType === PROPERTY_SEARCH_TYPES.ADDRESS_REPORT}
            searchTypes={
              searchType === PROPERTY_SEARCH_TYPES.PRE_FORECLOSURES
                ? ['city', 'state', 'zip']
                : []
            }
            parentFormSubmitHandler={handleRapidsDataSubmit}
            inputOptions={{
              hideInputLabel: true,
              hideSearchButton: true,
              autoFocus: true,
              placeholder,
              inputWidth: { md: '99%', xs: '99%' },
              customStyles: rapidsInputCustomStyles(theme),
              InputProps: {
                sx: { borderRadius: 3 },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: errors.address
                          ? theme.palette.error.main
                          : theme.palette.primary.main,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      );
    };

    const EnhancedRapidsInput = composeHOC(
      withErrorHandling,
      withClearButton
    )(BaseRapidsInput);

    return (
      <EnhancedRapidsInput
        className="property-search-input"
        error={errors.address}
        clearErrors={clearErrors}
        onClear={onClearHandler}
        value={value}
        fieldName="address"
        clearButtonStyles={{ right: 16 }}
      />
    );
  }
);
